import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  Activity,
  ArrowRight,
  ChevronDown,
  Clock,
  DollarSign,
  Flag,
  Gauge,
  Hash,
  Layers,
  Lightbulb,
  MapPin,
  MessageCircle,
  Scale,
  Share2,
  ShieldCheck,
  Sparkles,
  Square,
  Target,
  TrendingUp,
  Users,
  Volume2,
  X,
  Zap,
} from "lucide-react";
import {
  PHASE_LABEL,
  askPrompts,
  flowLayers,
  type BeatPhase,
  type DeepDive,
  type FactIcon,
} from "./deepDive";
import type { TrustLayer } from "./types";
import { displayFacts } from "./plateFacts";
import "./dive.css";

/**
 * The Deep Dive overlay: an immersive, focused canvas that shows HOW a story flows rather than
 * restating it. A one-line throughline, a scrollable narrative thread (typed beats that draw
 * themselves in, with the connector line drawing alongside), a beat progress rail on desktop
 * (dots + a gold fill that tracks reading position, tap a dot to jump), a "connect the dots"
 * causal map whose nodes are tappable (tap one to light its chain and dim the rest), a strip of
 * grounded key facts, a Listen pill that narrates the takeaway and beats, and an Ask section
 * that hands off to the real Ask AI. Calm reading-desk styling; motion is reduced-motion gated.
 * No em dashes anywhere.
 */

const PHASE_ICON: Record<BeatPhase, typeof Layers> = {
  background: Layers,
  trigger: Zap,
  event: Activity,
  impact: Target,
  why: Lightbulb,
  next: Flag,
};

const FACT_ICON: Record<FactIcon, typeof Hash> = {
  number: Hash,
  place: MapPin,
  time: Clock,
  money: DollarSign,
  people: Users,
  scale: Gauge,
  spark: Zap,
  flag: Flag,
  trend: TrendingUp,
};

/** Prefer named AU voices, then any en-AU, then en-GB, then any English (mirrors the app's
 *  per-card Listen; kept inline here so the panel stays self-contained). */
function pickNarrationVoice(): SpeechSynthesisVoice | null {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const auNamed = voices.find(
    (v) => /karen|catherine|james|lee|matilda|russell|jenny.*au|aria.*au/i.test(v.name) && v.lang.toLowerCase().startsWith("en"),
  );
  if (auNamed) return auNamed;
  const enAU = voices.find((v) => v.lang.toLowerCase().startsWith("en-au"));
  if (enAU) return enAU;
  const enGB = voices.find((v) => v.lang.toLowerCase().startsWith("en-gb"));
  if (enGB) return enGB;
  return voices.find((v) => v.lang.toLowerCase().startsWith("en")) ?? null;
}

export default function DeepDive({
  title,
  category,
  deepDive,
  sources,
  wholePicture,
  whyItMatters,
  story,
  storyQuote,
  tags,
  onFeedback,
  onClose,
  onAsk,
  onShare,
}: {
  title: string;
  category?: string;
  deepDive: DeepDive;
  sources: number;
  wholePicture?: TrustLayer;
  /* THE CHAPTERS THE CARD FRONT GAVE UP (2026-09-13, the simpler card). The front is the
     headline, the essence and three or four pointers; Why it matters, the story (the fuller
     read, or the body as paragraphs), the tags and the feedback line read here as chapters.
     Every prop is optional and a dive given none of them renders exactly as before. */
  whyItMatters?: string;
  story?: string[];
  storyQuote?: { text: string; speaker?: string } | null;
  tags?: string[];
  onFeedback?: () => void;
  onClose: () => void;
  onAsk: (question?: string) => void;
  onShare: () => void;
}) {
  const flow = useMemo(() => flowLayers(deepDive.map), [deepDive.map]);
  const prompts = useMemo(() => askPrompts(deepDive), [deepDive]);

  // Beat progress rail state: which beat the reader is on (drives the dots + gold fill).
  const [activeBeat, setActiveBeat] = useState(0);
  // Causal map focus: the id of the tapped node, or null when the whole map is lit.
  const [mapFocus, setMapFocus] = useState<string | null>(null);
  // Listen pill state: whether the dive is currently being narrated.
  const [listening, setListening] = useState(false);
  // "The whole picture" is a sibling lens to the dive (both explain the story), so it rides
  // INSIDE the dive as one reveal: a button that opens the settled / disputed / perspectives.
  const [wpOpen, setWpOpen] = useState(false);
  const hasWholePicture = !!wholePicture && (wholePicture.perspectives?.length ?? 0) >= 2;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const beatRefs = useRef<(HTMLLIElement | null)[]>([]);
  // Utterances must stay referenced while queued (Chrome can GC them mid-speech otherwise),
  // and a sequence counter keeps stale onend/onerror callbacks from flipping fresh state.
  const uttersRef = useRef<SpeechSynthesisUtterance[]>([]);
  const speechSeq = useRef(0);

  const speechOk = typeof window !== "undefined" && Boolean(window.speechSynthesis);

  const stopListening = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    speechSeq.current += 1;
    window.speechSynthesis.cancel();
    uttersRef.current = [];
    setListening(false);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Any narration stops the moment the overlay goes away (close button, scrim, Esc, unmount).
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  // Reading position: the topmost beat that is at least 60% visible in the scroll area is the
  // active one. This drives highlighting only (no motion), so it stays on under reduced motion.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    const visible = new Set<number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = beatRefs.current.indexOf(entry.target as HTMLLIElement);
          if (idx < 0) continue;
          if (entry.isIntersecting) visible.add(idx);
          else visible.delete(idx);
        }
        if (visible.size) setActiveBeat(Math.min(...visible));
      },
      { root, threshold: 0.6 },
    );
    for (const el of beatRefs.current) if (el) io.observe(el);
    return () => io.disconnect();
  }, [deepDive.beats.length]);

  const scrollToBeat = useCallback((i: number) => {
    const el = beatRefs.current[i];
    if (!el) return;
    const still =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
  }, []);

  // Narrate the dive: the takeaway first, then each beat's heading and line, queued as one
  // utterance per chunk so a mid-listen Stop cancels cleanly.
  const toggleListen = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (listening) {
      stopListening();
      return;
    }
    const synth = window.speechSynthesis;
    speechSeq.current += 1;
    const seq = speechSeq.current;
    synth.cancel();
    const chunks: string[] = [];
    if (deepDive.takeaway) chunks.push(deepDive.takeaway);
    for (const b of deepDive.beats) chunks.push(`${b.heading}. ${b.line}`);
    if (!chunks.length) return;
    const voice = pickNarrationVoice();
    uttersRef.current = chunks.map((text, i) => {
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "en-AU";
      utter.rate = 1.02;
      if (voice) utter.voice = voice;
      if (i === chunks.length - 1) {
        utter.onend = () => {
          if (speechSeq.current === seq) {
            uttersRef.current = [];
            setListening(false);
          }
        };
      }
      utter.onerror = () => {
        if (speechSeq.current === seq) {
          uttersRef.current = [];
          setListening(false);
        }
      };
      return utter;
    });
    for (const utter of uttersRef.current) synth.speak(utter);
    setListening(true);
  }, [listening, stopListening, deepDive]);

  // Focus mode for the causal map: the tapped node plus its direct neighbours stay lit.
  const mapOn = useMemo(() => {
    if (!mapFocus) return null;
    const on = new Set<string>([mapFocus]);
    for (const e of deepDive.map.edges) {
      if (e.from === mapFocus) on.add(e.to);
      if (e.to === mapFocus) on.add(e.from);
    }
    return on;
  }, [mapFocus, deepDive.map.edges]);

  // Per gap between layers: does any edge crossing it touch the focused node? Lights the
  // connector rows that belong to the focused chain.
  const gapTouchesFocus = useMemo(() => {
    if (!mapFocus) return [] as boolean[];
    return flow.layers.slice(0, -1).map((layer, li) => {
      const above = new Set(layer.map((n) => n.id));
      const below = new Set(flow.layers[li + 1].map((n) => n.id));
      return deepDive.map.edges.some(
        (e) => (e.from === mapFocus || e.to === mapFocus) && above.has(e.from) && below.has(e.to),
      );
    });
  }, [mapFocus, flow.layers, deepDive.map.edges]);

  const railPct =
    deepDive.beats.length > 1 ? Math.round((activeBeat / (deepDive.beats.length - 1)) * 100) : 100;

  const hasWhy = !!whyItMatters?.trim();
  const hasStory = !!story && story.length > 0;
  const hasFacts = displayFacts(deepDive.keyFacts).facts.length > 0;
  // The chapter list, in the order it reads. Empty when the dive carries only the thread:
  // with nothing before the beats there is nothing to jump between.
  const chapters = useMemo(() => {
    const list: { id: string; label: string }[] = [];
    if (hasWhy) list.push({ id: "why", label: "Why it matters" });
    if (hasStory) list.push({ id: "story", label: "The story" });
    if (deepDive.beats.length) list.push({ id: "beats", label: `${deepDive.beats.length} beats` });
    if (deepDive.map.nodes.length > 1) list.push({ id: "map", label: "How it connects" });
    if (hasFacts) list.push({ id: "facts", label: "Facts" });
    return hasWhy || hasStory ? list : [];
  }, [hasWhy, hasStory, hasFacts, deepDive.beats.length, deepDive.map.nodes.length]);
  const jumpTo = useCallback((id: string) => {
    const root = scrollRef.current;
    const el = root?.querySelector<HTMLElement>(`[data-chapter="${id}"]`);
    if (!el) return;
    const still =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
  }, []);

  return (
    <div className="dd-overlay" role="dialog" aria-modal="true" aria-label="Deep Dive" onClick={onClose}>
      <div className="dd-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dd-close" onClick={onClose} aria-label="Close deep dive">
          <X size={18} aria-hidden="true" />
        </button>

        {/* Beat progress rail: one dot per beat + a gold fill tracking the reading position */}
        {deepDive.beats.length > 1 ? (
          <nav
            className="dive-rail"
            aria-label="Beat progress"
            style={{ "--dr-p": `${railPct}%` } as CSSProperties}
          >
            <span className="dr-track" aria-hidden="true"><span className="dr-fill" /></span>
            {deepDive.beats.map((b, i) => (
              <button
                key={i}
                type="button"
                className="dr-dot"
                data-active={i === activeBeat || undefined}
                aria-current={i === activeBeat ? "true" : undefined}
                aria-label={`Go to beat ${i + 1}: ${b.heading}`}
                onClick={() => scrollToBeat(i)}
              />
            ))}
          </nav>
        ) : null}

        <header className="dd-head">
          <span className="dd-eyebrow">
            <Sparkles size={12} aria-hidden="true" /> Deep Dive
            {category ? <span className="dd-eyebrow-cat">{category}</span> : null}
          </span>
          <h2 className="dd-title">{title}</h2>
          {deepDive.takeaway ? <p className="dd-takeaway">{deepDive.takeaway}</p> : null}
          <span className="dd-sub">
            The story in {deepDive.beats.length} beats, about {deepDive.seconds}s
          </span>
          {speechOk ? (
            <button
              type="button"
              className="dd-listen"
              data-live={listening || undefined}
              aria-pressed={listening}
              onClick={toggleListen}
            >
              {listening ? <Square size={11} aria-hidden="true" /> : <Volume2 size={13} aria-hidden="true" />}
              {listening ? "Stop" : "Listen"}
            </button>
          ) : null}
        </header>

        <div className="dd-scroll" ref={scrollRef}>
          {chapters.length > 1 ? (
            <nav className="dd-contents" aria-label="In this dive">
              {chapters.map((c) => (
                <button key={c.id} type="button" className="dd-contents-chip" onClick={() => jumpTo(c.id)}>
                  {c.label}
                </button>
              ))}
            </nav>
          ) : null}

          {/* Chapter one: why it matters, set as a deck. The one paragraph that says why the
              door was worth opening, so it comes first. */}
          {hasWhy ? (
            <section className="dd-section dd-why dd-first" data-chapter="why">
              <span className="dd-section-label">Why it matters</span>
              <p className="dd-why-p">{whyItMatters}</p>
            </section>
          ) : null}

          {/* Chapter two: the story itself, before the beats. A reader who opened the door
              came to READ; the shape of the story (the thread) follows the story. */}
          {hasStory ? (
            <section className={`dd-section dd-fuller dd-story${hasWhy ? "" : " dd-first"}`} data-chapter="story">
              <span className="dd-section-label">The story</span>
              {story!.map((para, i) => (
                <p className="dd-fuller-p" key={i}>{para}</p>
              ))}
              {storyQuote?.text ? (
                <figure className="dd-fuller-quote">
                  <blockquote>{storyQuote.text}</blockquote>
                  {storyQuote.speaker ? <figcaption>{storyQuote.speaker}</figcaption> : null}
                </figure>
              ) : null}
            </section>
          ) : null}

          {/* The narrative thread */}
          <section className={`dd-section dd-beats${hasWhy || hasStory ? "" : " dd-first"}`} data-chapter="beats">
          {hasWhy || hasStory ? <span className="dd-section-label">How it unfolded</span> : null}
          <ol className="dd-thread">
            {deepDive.beats.map((b, i) => {
              const Icon = PHASE_ICON[b.phase];
              return (
                <li
                  key={i}
                  className="dd-beat"
                  style={{ animationDelay: `${i * 90}ms` }}
                  ref={(el) => { beatRefs.current[i] = el; }}
                >
                  <span className="dd-node" aria-hidden="true"><Icon size={15} /></span>
                  <div className="dd-beat-body">
                    <span className="dd-phase">{PHASE_LABEL[b.phase]}</span>
                    <strong className="dd-heading">{b.heading}</strong>
                    <p className="dd-line">{b.line}</p>
                  </div>
                </li>
              );
            })}
          </ol>
          </section>

          {/* Connect the dots: a causal flow with typed nodes + labeled links. Nodes are
              tappable: tapping one lights it (gold ring) plus its direct connections and dims
              the rest; tapping it again, or the map background, clears the focus. */}
          {deepDive.map.nodes.length > 1 ? (
            <section className="dd-section" data-chapter="map">
              <span className="dd-section-label">How it connects</span>
              <div
                className={`dd-flow${mapFocus ? " map-active" : ""}`}
                onClick={() => setMapFocus(null)}
              >
                {flow.layers.map((layer, li) => (
                  <div key={li} className="dd-flow-step">
                    <div className="dd-flow-row">
                      {layer.map((node) => (
                        <button
                          key={node.id}
                          type="button"
                          className={`dd-flow-node kind-${node.kind ?? "effect"}${mapOn?.has(node.id) ? " mn-on" : ""}`}
                          aria-pressed={mapFocus === node.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMapFocus((cur) => (cur === node.id ? null : node.id));
                          }}
                        >
                          <span className="dd-flow-label">{node.label}</span>
                          {node.caption ? <span className="dd-flow-caption">{node.caption}</span> : null}
                        </button>
                      ))}
                    </div>
                    {li < flow.layers.length - 1 ? (
                      <div
                        className={`dd-flow-link${gapTouchesFocus[li] ? " me-on" : ""}`}
                        aria-hidden="true"
                      >
                        <span className="dd-flow-line" />
                        {flow.linkLabels[li] ? <span className="dd-flow-verb">{flow.linkLabels[li]}</span> : null}
                        <span className="dd-flow-arrow" />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {/* Key facts. A strip of FIGURES when the dive authored real ones, a list of LINES
              when it did not: the source-only fallback emits whole sentences under the label
              "Reported fact 1", and a sentence set at 18px in a 142px column under a gold
              REPORTED FACT 1 is not a key fact, it is debug output. See plateFacts.ts. */}
          {(() => {
            const { mode, facts } = displayFacts(deepDive.keyFacts);
            if (!facts.length) return null;
            return (
              <section className="dd-section" data-chapter="facts">
                <span className="dd-section-label">{mode === "lines" ? "What was reported" : "Key facts"}</span>
                <div className={`dd-facts${mode === "lines" ? " is-lines" : ""}`}>
                  {facts.map((f, i) => {
                    if (mode === "lines") {
                      return (
                        <div key={i} className="dd-fact is-line">
                          <span className="dd-fact-value">{f.value}</span>
                          {f.hint ? <span className="dd-fact-hint">{f.hint}</span> : null}
                        </div>
                      );
                    }
                    const FIcon = FACT_ICON[(f.icon as keyof typeof FACT_ICON) ?? "number"] ?? FACT_ICON.number;
                    return (
                      <div key={i} className="dd-fact">
                        <span className="dd-fact-icon" aria-hidden="true"><FIcon size={15} /></span>
                        <span className="dd-fact-value">{f.value}</span>
                        {f.label ? <span className="dd-fact-label">{f.label}</span> : null}
                        {f.hint ? <span className="dd-fact-hint">{f.hint}</span> : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* The whole picture: the same trust layer that backs the story, folded in as one
              reveal so the dive shows both HOW it flows and the SPREAD of views behind it. */}
          {hasWholePicture ? (
            <section className="dd-section dd-wp">
              <span className="dd-section-label"><Scale size={13} aria-hidden="true" /> The whole picture</span>
              <button
                type="button"
                className="dd-wp-toggle"
                aria-expanded={wpOpen}
                onClick={() => setWpOpen((o) => !o)}
              >
                <span className="dd-wp-toggle-text">
                  {wpOpen ? "Hide the spread of views" : "See the spread of views"}
                  <em>{wholePicture!.perspectives.length} perspectives</em>
                </span>
                <ChevronDown size={16} aria-hidden="true" className={wpOpen ? "is-open" : ""} />
              </button>
              {wpOpen ? (
                <div className="dd-wp-body">
                  {wholePicture!.settled.length ? (
                    <div className="dd-wp-block">
                      <span className="dd-wp-label wp-settled">What's settled</span>
                      <ul>{wholePicture!.settled.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  ) : null}
                  {wholePicture!.disputed.length ? (
                    <div className="dd-wp-block">
                      <span className="dd-wp-label wp-disputed">What's disputed</span>
                      <ul>{wholePicture!.disputed.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  ) : null}
                  <div className="dd-wp-views">
                    {wholePicture!.perspectives.map((p, i) => (
                      <div key={i} className="dd-wp-view">
                        <strong>{p.label}</strong>
                        <span>{p.point}</span>
                        {p.sourceName ? <em>{p.sourceName}</em> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {/* Ask anything: hands off to the real Ask AI (grounded in this story's sources) */}
          <section className="dd-section">
            <span className="dd-section-label">Ask anything</span>
            <button type="button" className="dd-ask-launch" onClick={() => onAsk()}>
              <span className="dd-ask-orb" aria-hidden="true"><MessageCircle size={17} /></span>
              <span className="dd-ask-launch-text">
                <span className="dd-ask-launch-title">Ask about this story</span>
                <span className="dd-ask-launch-sub">Answers use only this story's sources</span>
              </span>
              <span className="dd-ask-launch-go" aria-hidden="true"><ArrowRight size={16} /></span>
            </button>
            {prompts.length ? (
              <div className="dd-ask-chips">
                {prompts.map((q, i) => (
                  <button key={i} type="button" className="dd-ask-chip" onClick={() => onAsk(q)}>
                    {q}
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          {/* Filed under: the tags that used to run under the card's byline. */}
          {tags && tags.length ? (
            <div className="dd-filed" aria-label="Filed under">
              <span className="dd-filed-label">Filed under</span>
              {tags.map((tag) => (
                <span key={tag} className="dd-tag">{tag}</span>
              ))}
            </div>
          ) : null}

          <footer className="dd-foot">
            <span className="dd-sources">
              <ShieldCheck size={13} aria-hidden="true" /> Grounded in {sources} {sources === 1 ? "source" : "sources"}
              {onFeedback ? (
                <button type="button" className="dd-feedback" onClick={onFeedback}>
                  Feedback on this story
                </button>
              ) : null}
            </span>
            <div className="dd-foot-actions">
              <button type="button" className="dd-share" onClick={onShare}>
                <Share2 size={15} aria-hidden="true" /> Share
              </button>
              <button type="button" className="dd-done" onClick={onClose}>Done</button>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
